import store from '@/store'
import Config from '@/config/vue.config'
import i18n from '@/i18n'
import { getViewerToken, getWebappToken } from '@/utils/token'

const userApiBaseUrl = Config.userServerUrl + '/api/v1'
const appGatewayApiBaseUrl = Config.appGatewayServerUrl + '/api/v1'

const onUnauthorizedDefault = (errorCode?: number) => {
  // 現在のパスを保存（ログイン成功後のリダイレクト用）
  const currentPath = window.location.pathname + window.location.search
  if (window.location.pathname !== '/login') {
    sessionStorage.setItem('redirect_after_login', currentPath)
  }

  store.dispatch('session/logoutExpired', errorCode)
  store.dispatch('pages/moveToLogin')
}

const rejectError = (onUnauthorized: (errorCode?: number) => void) => {
  return (response: Response) => {
    if (!response.ok) {
      const body = response.json()
      if (response.status == 401) {
        // トークンを無効化するかはerrorCodeで判定するので、bodyを読んでから呼ぶ
        // (bodyがJSONでなくてもログアウト処理自体は行う)
        body
          .then(
            (err) => onUnauthorized(err?.payload?.errorCode),
            () => onUnauthorized()
          )
          // このPromiseは誰も待たないので、onUnauthorizedが失敗してもunhandled rejectionにしない
          .catch(handleError)
      }
      throw body.then(function (err) {
        throw Error(err.payload.message)
      })
    }
    return response
  }
}

const handleError = (error: any) => {
  console.debug(error) // eslint-disable-line no-console
  return error
}

export default {
  Request: {
    streamerUserOwnerGet: () => {
      return {
        url: userApiBaseUrl + '/streamer_users/owner/get',
        method: 'POST',
      }
    },
    streamerUserWebLogin: (sns_kind: number) => {
      return {
        url: userApiBaseUrl + '/streamer_users/web/login',
        method: 'POST',
        params: { sns_kind: sns_kind },
      }
    },
    streamerGetFromProfileId: (profileId: string) => {
      return {
        url: appGatewayApiBaseUrl + '/streamer/get_from_profile_id',
        method: 'POST',
        params: {
          profileId: profileId,
        },
      }
    },
    streamerFollowExist: (vliveId: string) => {
      return {
        url: appGatewayApiBaseUrl + '/streamer/follow/exists',
        method: 'POST',
        params: {
          vliveId: vliveId,
        },
      }
    },
    streamerFollow: (vliveId: string) => {
      return {
        url: appGatewayApiBaseUrl + '/streamer/follow',
        method: 'POST',
        params: {
          targetVliveId: vliveId,
        },
      }
    },
    streamerUnfollow: (vliveId: string) => {
      return {
        url: appGatewayApiBaseUrl + '/streamer/unfollow',
        method: 'POST',
        params: {
          targetVliveId: vliveId,
        },
      }
    },
    depositAccountGet: () => {
      return {
        url: userApiBaseUrl + '/deposit_account/get',
        method: 'POST',
      }
    },
    depositAccountUpdate: (params: any) => {
      return {
        url: userApiBaseUrl + '/deposit_account/update',
        method: 'POST',
        params: params,
      }
    },
    depositAccountBankGet: () => {
      return {
        url: userApiBaseUrl + '/deposit_account/bank/get',
        method: 'POST',
      }
    },
    depositAccountBankUpdate: (params: any) => {
      return {
        url: userApiBaseUrl + '/deposit_account/bank/update',
        method: 'POST',
        params: params,
      }
    },
    liveCoinDepositHistoriesGet: () => {
      return {
        url: userApiBaseUrl + '/live_coins/deposit/history/get',
        method: 'POST',
      }
    },
    masterBankSearchByBankNameInitials: (initial: any) => {
      return {
        url: userApiBaseUrl + '/master_bank/search_by/bank_name_initials',
        method: 'POST',
        params: { initial: initial },
      }
    },
    giftCardList: () => {
      return {
        url: appGatewayApiBaseUrl + '/gift_card/list',
        // url: 'https://appgateway-naoya-dot-vlive-lab.appspot.com/api/v1' + '/gift_card/list',
        method: 'POST',
      }
    },
    giftCardIssue: (params: any) => {
      return {
        url: appGatewayApiBaseUrl + '/gift_card/issue',
        // url: 'https://appgateway-naoya-dot-vlive-lab.appspot.com/api/v1' + '/gift_card/issue',
        method: 'POST',
        params: params,
      }
    },
    giftCardHistoryGet: () => {
      return {
        url: appGatewayApiBaseUrl + '/gift_card/history/get',
        // url: 'https://appgateway-naoya-dot-vlive-lab.appspot.com/api/v1' + '/gift_card/history/get',
        method: 'POST',
      }
    },
    liveCoinTotalTransferHistoriesList: () => {
      return {
        url:
          appGatewayApiBaseUrl +
          '/streamer/currency/live_point/transfer/history/list/yearly',
        method: 'POST',
      }
    },
    presentGetFromCode: (code: string) => {
      return {
        url: appGatewayApiBaseUrl + '/present/code/get',
        method: 'POST',
        params: { code: code },
      }
    },
    presentReceiveCode: (code: string) => {
      return {
        url: appGatewayApiBaseUrl + '/present/code/receive',
        method: 'POST',
        params: { code: code },
      }
    },
    liveList: () => {
      return {
        url: appGatewayApiBaseUrl + '/live/list',
        method: 'POST',
        headers: {
          'X-VLIVE-OS': 'web',
          'X-VLIVE-CLIENT-VERSION': '9999.0.0',
        },
        params: {},
      }
    },
    liveCategoryLoad: (category: string, page: number = 0) => {
      return {
        url: appGatewayApiBaseUrl + '/live/category/load',
        method: 'POST',
        headers: {
          'X-VLIVE-OS': 'web',
          'X-VLIVE-CLIENT-VERSION': '9999.0.0',
        },
        params: {
          page: page,
          targetCategory: category,
        },
      }
    },
    liveCommentSend: (mediaId: string, message: string) => {
      return {
        url: appGatewayApiBaseUrl + '/live/comment',
        method: 'POST',
        params: {
          mediaId: mediaId,
          comment: message,
        },
      }
    },
    liveStartViewingSend: (mediaId: string) => {
      return {
        url: appGatewayApiBaseUrl + '/live/start_viewing',
        method: 'POST',
        params: {
          mediaId: mediaId,
        },
      }
    },
    mediaGet: (mediaId: number) => {
      return {
        url: appGatewayApiBaseUrl + '/media/get',
        method: 'POST',
        params: {
          mediaId: mediaId,
        },
      }
    },
    mediaLike: (mediaId: number, likeCount: number) => {
      return {
        url: appGatewayApiBaseUrl + '/media/like/send',
        method: 'POST',
        params: {
          mediaId: mediaId,
          count: likeCount,
        },
      }
    },
  },
  load: (
    request: {
      url: string
      method: string
      params?: any
      headers?: Record<string, string>
    },
    onUnauthorized = onUnauthorizedDefault
  ) => {
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept-Language': i18n.locale,
      'X-VLIVE-CLIENT-TYPE': 'webapp',
    }
    const viewerToken = getViewerToken()
    const webappToken = getWebappToken()

    if (viewerToken) {
      defaultHeaders['Authorization'] = `Bearer ${viewerToken}`
    } else if (webappToken) {
      defaultHeaders['Authorization'] = `Bearer ${webappToken}`
    }

    return fetch(request.url, {
      method: request.method,
      headers: Object.assign(defaultHeaders, request.headers),
      body: JSON.stringify(request.params),
      mode: 'cors',
    })
      .then(rejectError(onUnauthorized))
      .then((r) => r.json())
      .then((json) => json.payload)
      .catch(handleError)
  },
}
